import { AccountType } from "@common/constants/domains/accounting/accounts";
import { AllocateCodeText, DraftState } from "./draftState";
import {
    applyDraftSortOrders,
    orderedScopeIdsInScope,
    orderedScopeMembers,
    planDraftScopePlacement,
} from "./draftSortOrder";
import { clearIneligibleForm1099Box } from "./draftForm1099Box";
import { retypeGroupSubtrees } from "./draftSubtreeRetype";

// The kind of row a drag gesture moves. Groups keep their account type; only
// custom accounts may change leaf.
export type MoveRowKind = "account" | "group";

// Whether the dragged row lands immediately before or after `overRefId`.
export type MoveInsertPosition = "before" | "after";

// A validated drop, expressed independently of the gesture: the dragged row,
// its destination scope, and the sibling it should sit beside (`overRefId`,
// null = append at the end of the destination scope). `overRefId` may be an
// ACCOUNT or a GROUP id — accounts and groups share one ordering space, so a
// group can land between two accounts (and vice versa). `position` places the
// row before/after that sibling; when omitted the legacy drag-direction
// (arrayMove) placement is used for backward compatibility.
export interface MoveRowArgs {
    draggedId: string;
    draggedKind: MoveRowKind;
    entityId: number;
    targetType: AccountType;
    targetParentGroupId: string | null;
    overRefId: string | null;
    position?: MoveInsertPosition;
}

// Pure list splice: remove the item at `from`, reinsert it at `to`. Mirrors
// dnd-kit's arrayMove without coupling this draft op to the dnd library.
const moveItem = <T>(items: T[], from: number, to: number): T[] => {
    const copy = [...items];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
};

// Final ordered ids for the destination scope after the dragged id lands at the
// slot implied by `overRefId` + `position`. When `position` is provided the row
// is spliced immediately before/after the reference (precise positional drop).
// When omitted, same-scope reorders mirror arrayMove (drag-direction placement)
// and cross-scope moves splice in before the reference — the legacy behaviour.
const resolveDestinationOrder = ({
    draggedId,
    destinationOrder,
    overRefId,
    position,
    isSameScope,
}: {
    draggedId: string;
    destinationOrder: string[];
    overRefId: string | null;
    position: MoveInsertPosition | undefined;
    isSameScope: boolean;
}): string[] => {
    if (position) {
        const without = destinationOrder.filter((id) => id !== draggedId);
        if (!overRefId) {
            return [...without, draggedId];
        }
        const refIndex = without.indexOf(overRefId);
        if (refIndex === -1) {
            return [...without, draggedId];
        }
        const insertIndex = position === "after" ? refIndex + 1 : refIndex;
        return [
            ...without.slice(0, insertIndex),
            draggedId,
            ...without.slice(insertIndex),
        ];
    }
    // Compatibility seam: positional callers always pass `position`, so this
    // drag-direction arrayMove path only runs for legacy callers that omit it.
    if (isSameScope) {
        const oldIndex = destinationOrder.indexOf(draggedId);
        const refIndex = overRefId ? destinationOrder.indexOf(overRefId) : -1;
        const newIndex =
            refIndex === -1 ? destinationOrder.length - 1 : refIndex;
        return moveItem(destinationOrder, oldIndex, newIndex);
    }
    const without = destinationOrder.filter((id) => id !== draggedId);
    const refIndex = overRefId ? without.indexOf(overRefId) : -1;
    const insertIndex = refIndex === -1 ? without.length : refIndex;
    return [
        ...without.slice(0, insertIndex),
        draggedId,
        ...without.slice(insertIndex),
    ];
};

export const moveRow = ({
    state,
    allocateCodeText,
    ...args
}: {
    state: DraftState;
    allocateCodeText: AllocateCodeText;
} & MoveRowArgs): DraftState => {
    const draggedAccount =
        args.draggedKind === "account"
            ? state.accounts.find((row) => row.id === args.draggedId)
            : undefined;
    const draggedGroup =
        args.draggedKind === "group"
            ? state.groups.find((row) => row.id === args.draggedId)
            : undefined;
    const dragged = draggedAccount ?? draggedGroup;
    if (!dragged) {
        return state;
    }

    const sourceType = dragged.type;
    const sourceParentGroupId = dragged.parentGroupId;
    const isSameScope =
        sourceType === args.targetType &&
        sourceParentGroupId === args.targetParentGroupId;

    const destinationOrder = resolveDestinationOrder({
        draggedId: args.draggedId,
        destinationOrder: orderedScopeIdsInScope({
            accounts: state.accounts,
            groups: state.groups,
            entityId: args.entityId,
            type: args.targetType,
            parentGroupId: args.targetParentGroupId,
        }),
        overRefId: args.overRefId,
        position: args.position,
        isSameScope,
    });
    const assignments = planDraftScopePlacement({
        members: orderedScopeMembers({
            accounts: state.accounts,
            groups: state.groups,
            entityId: args.entityId,
            type: args.targetType,
            parentGroupId: args.targetParentGroupId,
        }),
        placed: [
            {
                id: args.draggedId,
                kind: args.draggedKind,
                name: dragged.name,
            },
        ],
        // destinationOrder still contains the mover at its landing slot.
        insertIndex: destinationOrder.indexOf(args.draggedId),
    });

    // Exact type changes re-run the injected allocator because the old code may
    // fall outside the new range; same-type moves keep the current codeText.
    const isTypeChange =
        draggedAccount !== undefined && sourceType !== args.targetType;
    const nextCodeText =
        draggedAccount && isTypeChange
            ? allocateCodeText({
                  kind: "relocate",
                  type: args.targetType,
                  accounts: state.accounts,
                  currentCodeText: draggedAccount.codeText,
              })
            : (draggedAccount?.codeText ?? null);

    const isGroupTypeChange =
        draggedGroup !== undefined && sourceType !== args.targetType;
    const { descendantGroupIds, codeByAccountId } = isGroupTypeChange
        ? retypeGroupSubtrees({
              state,
              rootGroupIds: [args.draggedId],
              targetType: args.targetType,
              allocateCodeText,
              initialScanAccounts: state.accounts,
          })
        : {
              descendantGroupIds: new Set<string>(),
              codeByAccountId: new Map<string, string | null>(),
          };

    const accounts = state.accounts.map((row) => {
        if (draggedAccount && row.id === args.draggedId) {
            const moved = {
                ...row,
                type: args.targetType,
                parentGroupId: args.targetParentGroupId,
                codeText: nextCodeText,
            };
            return isTypeChange ? clearIneligibleForm1099Box(moved) : moved;
        }
        if (codeByAccountId.has(row.id)) {
            return clearIneligibleForm1099Box({
                ...row,
                type: args.targetType,
                codeText: codeByAccountId.get(row.id) ?? null,
            });
        }
        return row;
    });
    const groups = state.groups.map((row) => {
        if (draggedGroup && row.id === args.draggedId) {
            return {
                ...row,
                type: args.targetType,
                parentGroupId: args.targetParentGroupId,
            };
        }
        if (descendantGroupIds.has(row.id)) {
            return { ...row, type: args.targetType };
        }
        return row;
    });
    return {
        ...state,
        accounts: applyDraftSortOrders(accounts, assignments),
        groups: applyDraftSortOrders(groups, assignments),
    };
};
